import ServiceWorkerService from '../../services/ServiceWorkerService';
import utilService from '../../services/UtilService';
import cacheManager, { cacheContexts } from './cacheManager';
import loadProgressHandler from './loadProgressHandler';
import apiTools from '../../services/api/ApiTools';

const packagePromises = {};

function messageListener(event) {
    if (event.data.type === 'cache-file-package-result') {
        try {
            const { resultPackage } = event.data;
            const resolver = packagePromises[resultPackage.id];

            resolver(resultPackage);
        } catch (error) {
            window.console.error(error);
        }
    } else if (event.data.type === 'cache-file-progress') {
        const { logicalName, url, loaded, total, identifier, isDone, hasFailed } = event.data;
        if (hasFailed && !cacheManager.hasAborted()) {
            // hasFailed is triggered while SW was reading the load progress with ReadableStream -> network error
            apiTools.handlePossibleOnlineStatusChange(false, true);
        }
        loadProgressHandler.addProgressData(logicalName, url, loaded, total, identifier, isDone, hasFailed);
    } else if (event.data.type === 'readable-stream-health-response') {
        ServiceWorkerService.clearServiceWorkerValidation();
    } else if (event.data.swError !== undefined) {
        window.console.error('SW cache error', event.data.swError);
    }
}

export function byCacheCreatedTime(direction = 'ascending') {
    const comparator = direction === 'descending'
        ? (a, b) => b - a
        : (a, b) => a - b;

    return function compareDates(a, b) {
        const regEx = /created_(.+)/i;
        const aMatches = regEx.exec(a);
        const bMatches = regEx.exec(b);

        if (aMatches && bMatches) {
            return comparator(new Date(aMatches[1]), new Date(bMatches[1]));
        }
        return 0;
    };
}

export const cacheTypes = {
    test: { prefix: 'TEST_CACHE', hasTime: false, identifierName: 'assessmentRunId' },
    readiness_check: { prefix: 'READINESS_CHECK', hasTime: true, identifierName: 'contentRevisionId', maxCount: 2 },
    info_docs: { prefix: 'INFO_DOCS', hasTime: true, identifierName: 'sourceTemplateId', maxCount: 2 },
    general_resources: { prefix: 'GENERAL_RESOURCES', hasTime: false, identifierName: null },
};

const cacheKeysIndex = {};

const serviceWorkerCacheHandler = {
    name: 'serviceWorkerCacheHandler',
    init() {
        navigator.serviceWorker.addEventListener('message', messageListener, false);
        ServiceWorkerService.addServiceWorkerHealthMonitor();
        return serviceWorkerCacheHandler;
    },

    cancel() {
        navigator.serviceWorker.removeEventListener('message', messageListener);
    },

    async cacheFilePackageInOneGo(filePackage, cacheContext, questionSetIdentifier) {
        const pkg = { ...filePackage };
        pkg.cacheName = await this.getCacheName(cacheContext, questionSetIdentifier);

        return new Promise((resolve) => {
            const message = {
                type: 'cache-file-package',
                filePackage: pkg,
            };

            packagePromises[filePackage.id] = (resultPackage) => {
                if (resultPackage.success === false) {
                    console.error('Error trying to cache files in SW.', resultPackage);
                }
                resolve(resultPackage);
            };
            navigator.serviceWorker.controller.postMessage(message);
        });
    },

    async cacheFilePackage(filePackage, cacheContext, questionSetIdentifier) {
        const { capabilities } = await ServiceWorkerService.getServiceWorkerInfo();
        const resultPackage = { filePackage, files: [] };
        if (capabilities?.singleFileCacheRequests) {
            return Promise
                .all(filePackage.files.map((fileModel) => {
                    return this
                        .cacheSingleFile(fileModel, cacheContext, questionSetIdentifier)
                        .then((fileResult) => {
                            if (fileResult.success === false) {
                                resultPackage.success = false;
                            }
                            resultPackage.files.push(fileResult);
                        });
                }))
                .then(() => resultPackage);
        }

        return this.cacheFilePackageInOneGo(filePackage, cacheContext, questionSetIdentifier);
    },

    async cacheSingleFile(fileModel, cacheContext, questionSetIdentifier) {
        const cacheName = await this.getCacheName(cacheContext, questionSetIdentifier);
        const result = await ServiceWorkerService.postMessageAndAwaitResult(
            'cache-single-file',
            { fileModel, cacheName, identifier: questionSetIdentifier.paramValue },
        );
        if (result.success === false) {
            console.error('Error trying to cache file in SW.', result);
        }
        return result;
    },

    async getCacheName(cacheContext, questionDataIdentifier = {}) {
        if (cacheContext === cacheContexts.TEST) {
            return `TEST_CACHE_${questionDataIdentifier.paramName}_${questionDataIdentifier.paramValue}`;
        }
        if (cacheContext === cacheContexts.READINESS_CHECK) {
            const existingCacheKey = await this.getExistingCacheNameForReadinessCheck(questionDataIdentifier.paramValue);
            if (existingCacheKey) {
                return existingCacheKey;
            }
            const keyRoot = `READINESS_CHECK_contentRevisionId_${questionDataIdentifier.paramValue}_`;
            const newCacheKey = `${keyRoot}created_${utilService.getIsoDateStamp(new Date())}`;
            cacheKeysIndex[newCacheKey] = newCacheKey;
            return newCacheKey;
        }

        if (cacheContext === cacheContexts.INFO_DOCS) {
            const existingCacheKey = await this.getExistingCacheNameForInfoDocs(questionDataIdentifier.paramValue);
            if (existingCacheKey) {
                return existingCacheKey;
            }
            const keyRoot = `INFO_DOCS_sourceTemplateId_${questionDataIdentifier.paramValue}_`;
            const newCacheKey = `${keyRoot}created_${utilService.getIsoDateStamp(new Date())}`;
            cacheKeysIndex[newCacheKey] = newCacheKey;
            return newCacheKey;
        }

        return 'GENERAL_RESOURCES';
    },

    abortResourceLoading() {
        navigator.serviceWorker.controller.postMessage({
            type: 'abortRequests',
        });
    },

    getCacheNameWithoutTime(type, identifier) {
        if (type.identifierName === null) {
            return type.prefix;
        }

        return `${type.prefix}_${type.identifierName}_${identifier}`;
    },

    async getAllCacheNamesOfType(type) {
        try {
            const keys = await caches.keys();
            return keys.filter(key => key.indexOf(`${type.prefix}_`) === 0);
        } catch (error) {
            console.error(error);
            return [];
        }
    },

    async getAllTestCacheNames() {
        return this.getAllCacheNamesOfType(cacheTypes.test);
    },

    async getAllReadinessCheckCacheNames() {
        return this.getAllCacheNamesOfType(cacheTypes.readiness_check);
    },

    async getAllInfoDocsCacheNames() {
        return this.getAllCacheNamesOfType(cacheTypes.info_docs);
    },

    async getExistingCacheNameForType(type, identifier) {
        try {
            const keyRoot = this.getCacheNameWithoutTime(type, identifier);

            if (cacheKeysIndex[keyRoot]) {
                return cacheKeysIndex[keyRoot];
            }

            const keys = await caches.keys();
            const liveKeyRoot = type.hasTime ? `${keyRoot}_` : keyRoot;
            const existingKey = keys.find(key => key.indexOf(liveKeyRoot) === 0);
            if (existingKey) {
                cacheKeysIndex[keyRoot] = existingKey;
            }
            return existingKey;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    },

    async getExistingCacheNameForReadinessCheck(assessmentRunId) {
        return this.getExistingCacheNameForType(cacheTypes.readiness_check, assessmentRunId);
    },

    async getExistingCacheNameForInfoDocs(templateId) {
        return this.getExistingCacheNameForType(cacheTypes.info_docs, templateId);
    },

    async makeRoomForANewCacheOfType(type) {
        const existingCacheNames = await this.getAllCacheNamesOfType(type);
        if (type.maxCount && existingCacheNames.length > type.maxCount) {
            return this.deleteAllButTheNewestReadinessCheckCaches(type.maxCount);
        }
        return undefined;
    },

    async makeRoomForANewReadinessCheckCache() {
        return this.makeRoomForANewCacheOfType(cacheTypes.readiness_check);
    },

    async makeRoomForANewInfoDocsCheckCache() {
        return this.makeRoomForANewCacheOfType(cacheTypes.info_docs);
    },

    /**
     * @param {number} numberToKeep
     * @return {Promise}
     */
    async deleteAllButTheNewestReadinessCheckCaches(numberToKeep) {
        const promises = this
            .getAllReadinessCheckCacheNames()
            .then((names) => {
                return names
                    .sort(byCacheCreatedTime('descending'))
                    .filter((cacheName, index) => index > numberToKeep - 1)
                    .map(cacheName => this.deleteCache(cacheName));
            });

        return Promise.all(promises);
    },

    async deleteCache(cacheName) {
        try {
            const success = await caches.delete(cacheName);
            if (success) {
                console.log(`${cacheName} was successfully deleted.`);
            } else {
                console.log(`${cacheName} was not deleted. It probably did not exist.`);
            }
            return success;
        } catch (error) {
            console.error(error);
            return false;
        }
    },

    async deleteCachesForOtherAssessments(assessmentRunIdsToKeep) {
        const allTestCacheNames = await this.getAllTestCacheNames();
        const regEx = /TEST_CACHE_assessmentRunId_([0-9]+)/i;
        const toDelete = allTestCacheNames
            .map((name) => {
                const matches = regEx.exec(name);
                return Array.isArray(matches) ? parseInt(matches[1], 10) : null;
            })
            .filter(assessmentRunId => !assessmentRunIdsToKeep.includes(assessmentRunId));

        return Promise.all(
            toDelete.map(assessmentRunId => this.deleteAssessmentDataCache(assessmentRunId))
        );
    },

    async deleteAssessmentDataCache(assessmentRunId) {
        const cacheName = await this.getCacheName(assessmentRunId);
        return this.deleteCache(cacheName);
    },

    async deleteAllAssessmentDataCaches() {
        const allTestCacheNames = await this.getAllTestCacheNames();
        return Promise.all(
            allTestCacheNames.map((cacheName) => {
                return this.deleteCache(cacheName);
            })
        );
    },
};

export default serviceWorkerCacheHandler;
